'use client';

import * as React from 'react';
import {
  useController,
  UseControllerProps,
  FieldValues,
  FieldPath,
  Control,
} from 'react-hook-form';

import { NumberInput, NumberInputProps } from '../number-input';

export interface FormNumberInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Omit<NumberInputProps, 'name' | 'defaultValue' | 'value' | 'onChange' | 'ref'> {
  /**
   * Field name for React Hook Form
   */
  name: TName;

  /**
   * Control from React Hook Form
   */
  control?: Control<TFieldValues>;

  /**
   * Validation rules for React Hook Form
   */
  rules?: UseControllerProps<TFieldValues, TName>['rules'];

  /**
   * Whether to show the error message
   * @default true
   */
  showError?: boolean;
}

/**
 * NumberInput component integrated with React Hook Form
 * Handles numeric values with validation
 */
export function FormNumberInput<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({ name, control, rules, showError = true, ...props }: FormNumberInputProps<TFieldValues, TName>) {
  const {
    field: { onChange, value, ref },
    fieldState: { error },
  } = useController({
    name,
    control,
    rules,
  });

  // Handle change to ensure numeric value
  const handleChange = React.useCallback(
    (newValue: number) => {
      // Handle NaN values
      if (isNaN(newValue)) {
        onChange(undefined);
      } else {
        onChange(newValue);
      }
    },
    [onChange]
  );

  return (
    <NumberInput
      {...props}
      ref={ref}
      value={value ?? undefined}
      onChange={handleChange}
      error={showError ? error?.message : undefined}
      aria-invalid={!!error}
      aria-describedby={error ? `${name}-error` : undefined}
    />
  );
}

FormNumberInput.displayName = 'FormNumberInput';
